import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  useTheme,
  CircularProgress,
  Button,
  TextField,
  Chip,
  Tooltip,
  ButtonGroup,
  Stack,
  Divider,
  Alert,
  Paper,
  LinearProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  ShoppingCart as OrderIcon,
  LocalShipping as DeliveredIcon,
  AssignmentReturn as ReturnIcon,
  HourglassEmpty as PendingIcon,
  AccountBalance as SettlementIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Remove as BreakEvenIcon,
  CurrencyRupee as RupeeIcon,
  Inventory as CostIcon,
  Campaign as MarketingIcon,
  Receipt as TaxIcon,
  LocalOffer as CommissionIcon,
  LocalShipping as ShippingIcon,
} from "@mui/icons-material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import { useDispatch } from "react-redux";
import { getMeeshoFinanceDashboard } from "app/redux/slice/meeshoOrderManagementSlice";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import moment from "moment";

const fmt = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const fmtDec = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [moment().startOf("day").toDate(), moment().endOf("day").toDate()],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [moment().subtract(6, "days").startOf("day").toDate(), moment().endOf("day").toDate()],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [moment().subtract(29, "days").startOf("day").toDate(), moment().endOf("day").toDate()],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [moment().startOf("year").toDate(), moment().endOf("day").toDate()],
  },
];

// ── Reusable sub-components ───────────────────────────────────────────────────

const SectionHeader = ({ title, subtitle }) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: "nowrap", color: "text.primary" }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap", fontWeight: 500 }}>
        {subtitle}
      </Typography>
    )}
    <Divider sx={{ flex: 1 }} />
  </Stack>
);

const MetricCard = ({ icon, iconColor, title, amount, subtitle, highlight = false, amountColor }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={highlight ? 3 : 1}
      sx={{
        height: "100%",
        border: highlight ? `2px solid ${iconColor}` : `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": { transform: "translateY(-2px)", boxShadow: 4 },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(iconColor, 0.12),
              flexShrink: 0,
            }}
          >
            {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 22 } })}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Tooltip title={title} placement="top-start">
              <Typography
                variant="body2"
                sx={{ display: "block", mb: 0.4, fontWeight: 600, color: "text.secondary" }}
                noWrap
              >
                {title}
              </Typography>
            </Tooltip>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, lineHeight: 1.2, color: amountColor || "text.primary" }}
            >
              {fmtDec(amount)}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{ mt: 0.4, display: "block", color: "text.secondary", fontWeight: 500 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const KpiChip = ({ label, value, color }) => (
  <Box sx={{ textAlign: "center", px: 2 }}>
    <Typography
      variant="h5"
      sx={{ fontWeight: 800, color: color || "text.primary", lineHeight: 1 }}
    >
      {value}
    </Typography>
    <Typography
      variant="body2"
      sx={{ display: "block", mt: 0.5, color: "text.secondary", fontWeight: 600, fontSize: "0.78rem" }}
    >
      {label}
    </Typography>
  </Box>
);

// ── Main Component ─────────────────────────────────────────────────────────────

const FinanceDashboard = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);

  const fetchData = useCallback(
    (params = {}) => {
      setLoading(true);
      dispatch(getMeeshoFinanceDashboard(params))
        .unwrap()
        .then((res) => {
          if (res?.status === 200) setData(res?.data?.data);
          else enqueueSnackbar("Failed to fetch finance data", { variant: "error" });
        })
        .catch(() => enqueueSnackbar("Failed to fetch finance data", { variant: "error" }))
        .finally(() => setLoading(false));
    },
    [dispatch, enqueueSnackbar]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApply = () => {
    if (!dateRange[0] || !dateRange[1]) {
      enqueueSnackbar("Please select both start and end date", { variant: "warning" });
      return;
    }
    fetchData({
      fromDate: moment(dateRange[0]).format("YYYY-MM-DD"),
      toDate: moment(dateRange[1]).format("YYYY-MM-DD"),
    });
  };

  const handleReset = () => {
    setDateRange([null, null]);
    fetchData();
  };

  // ── Derived values ──────────────────────────────────────────────────────────
  const stats = data?.orderStats || {};
  const rev = data?.revenue || {};
  const costs = data?.costs || {};
  const deductions = data?.deductions || {};
  const pending = data?.pending || {};
  const pl = data?.pl || {};

  const isProfit = pl.status === "PROFIT";
  const isLoss = pl.status === "LOSS";
  const plColor = isProfit
    ? theme.palette.success.main
    : isLoss
      ? theme.palette.error.main
      : theme.palette.warning.main;

  const returnPct = stats.returnPercentage || 0;
  const returnPctColor =
    returnPct > 30
      ? theme.palette.error.main
      : returnPct > 15
        ? theme.palette.warning.main
        : theme.palette.success.main;

  return (
    <Box>
      {/* ── Page Title ─────────────────────────────────────────────────────── */}
      <Typography variant="h4" component="h1" gutterBottom mb={2}>
        Finance Dashboard
      </Typography>

      {/* ── Date Filter ────────────────────────────────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={dateRange}
                  onChange={setDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} – ${endProps.inputProps.value || "DD/MM/YYYY"}`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <ButtonGroup size="small" variant="outlined">
                {QUICK_RANGES.map((r) => (
                  <Button key={r.key} onClick={() => setDateRange(r.getRange())} disabled={loading}>
                    {r.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                fullWidth
                size="small"
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={loading}
                startIcon={<RefreshIcon />}
                fullWidth
                size="small"
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Loader />
      ) : !data ? (
        <Alert severity="info">No data available. Select a date range and click Apply.</Alert>
      ) : (
        <Grid container spacing={3}>

          {/* ── Order Stats KPI Strip ─────────────────────────────────────── */}
          <Grid item xs={12}>
            <Card elevation={1}>
              <CardContent sx={{ py: 2 }}>
                <Grid container alignItems="center" justifyContent="space-around" divider>
                  <Grid item>
                    <KpiChip label="Total Orders" value={stats.totalOrders ?? 0} />
                  </Grid>
                  <Divider orientation="vertical" flexItem />
                  <Grid item>
                    <KpiChip
                      label="Delivered"
                      value={stats.totalDelivered ?? 0}
                      color={theme.palette.success.main}
                    />
                  </Grid>
                  <Divider orientation="vertical" flexItem />
                  <Grid item>
                    <KpiChip
                      label="Returns"
                      value={stats.totalReturns ?? 0}
                      color={theme.palette.error.main}
                    />
                  </Grid>
                  <Divider orientation="vertical" flexItem />
                  <Grid item>
                    <KpiChip
                      label="RTO"
                      value={stats.totalRTO ?? 0}
                      color={theme.palette.error.dark}
                    />
                  </Grid>
                  <Divider orientation="vertical" flexItem />
                  <Grid item>
                    <Tooltip title="(Returns + RTO) ÷ Delivered Orders" placement="top">
                      <Box>
                        <KpiChip
                          label="Return Rate"
                          value={`${returnPct}%`}
                          color={returnPctColor}
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Divider orientation="vertical" flexItem />
                  <Grid item>
                    <KpiChip
                      label="Pending Payment"
                      value={stats.pendingOrders ?? 0}
                      color={theme.palette.warning.main}
                    />
                  </Grid>
                  <Divider orientation="vertical" flexItem />
                  <Grid item>
                    <KpiChip
                      label="Other (Exchange)"
                      value={stats.totalOther ?? 0}
                      color={theme.palette.info.main}
                    />
                  </Grid>
                  {/* <Divider orientation="vertical" flexItem /> */}
                  {/* <Grid item>
                    <KpiChip
                      label="Paid Orders"
                      value={stats.paidOrders ?? 0}
                      color={theme.palette.primary.main}
                    />
                  </Grid> */}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* ── P&L Hero Card ─────────────────────────────────────────────── */}
          <Grid item xs={12}>
            <Card
              elevation={4}
              sx={{
                background: isProfit
                  ? `linear-gradient(135deg, #1b8a4a 0%, #0d5c32 100%)`
                  : isLoss
                    ? `linear-gradient(135deg, #c62828 0%, #7f0000 100%)`
                    : `linear-gradient(135deg, #e65100 0%, #bf360c 100%)`,
                color: "#fff",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container alignItems="center" spacing={3}>
                  {/* Icon */}
                  <Grid item>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid rgba(255,255,255,0.3)",
                      }}
                    >
                      {isProfit ? (
                        <TrendingUpIcon sx={{ fontSize: 36, color: "#fff" }} />
                      ) : isLoss ? (
                        <TrendingDownIcon sx={{ fontSize: 36, color: "#fff" }} />
                      ) : (
                        <BreakEvenIcon sx={{ fontSize: 36, color: "#fff" }} />
                      )}
                    </Box>
                  </Grid>

                  {/* Main P&L number */}
                  <Grid item xs>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "rgba(255,255,255,0.88)", fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", fontSize: "0.75rem" }}
                    >
                      Final Profit / Loss
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.15, my: 0.75, color: "#fff" }}>
                      {fmt(pl.amount)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.82)", mt: 0.5, fontWeight: 500 }}>
                      P/L = Net Settlement − Delivered Cost − Marketing &amp; Expense
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "#fff", mt: 0.5, fontWeight: 700, fontSize: "0.95rem", letterSpacing: 0.2 }}
                    >
                      {`= ${fmtDec(pl.netSettlement)} − ${fmtDec(pl.deliveredCost)} − ${fmtDec(pl.marketingExpense)}`}
                    </Typography>
                  </Grid>

                  {/* Status badge */}
                  <Grid item>
                    <Chip
                      label={pl.status || "N/A"}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.22)",
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: "0.95rem",
                        height: 40,
                        px: 1.5,
                        border: "2px solid rgba(255,255,255,0.6)",
                        letterSpacing: 1,
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* ── Revenue Section ───────────────────────────────────────────── */}
          <Grid item xs={12}>
            <SectionHeader title="Revenue" subtitle="Settlement received from Meesho" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  icon={<SettlementIcon />}
                  iconColor={theme.palette.success.main}
                  title="Net Settlement"
                  amount={rev.netSettlement}
                  subtitle="Delivered + Return + Other (all statuses)"
                  highlight
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  icon={<MonetizationOnIcon />}
                  iconColor={theme.palette.success.dark}
                  title="Delivery Settlement"
                  amount={rev.deliverySettlement}
                  subtitle={`${stats.totalDelivered ?? 0} delivered orders`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  icon={<ReturnIcon />}
                  iconColor={theme.palette.error.main}
                  title="Return Settlement"
                  amount={rev.returnSettlement}
                  subtitle={`${stats.totalReturns ?? 0} returned orders`}
                  amountColor={rev.returnSettlement < 0 ? theme.palette.error.main : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  icon={<ShippingIcon />}
                  iconColor={theme.palette.info.main}
                  title="Other Settlement"
                  amount={rev.otherSettlement}
                  subtitle={`Shipped / RTO / Exchange (${(stats.totalOther ?? 0) + (stats.totalRTO ?? 0)} orders)`}
                  amountColor={
                    rev.otherSettlement < 0
                      ? theme.palette.error.main
                      : rev.otherSettlement > 0
                        ? theme.palette.info.dark
                        : undefined
                  }
                />
              </Grid>
              {/* <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  icon={<RupeeIcon />}
                  iconColor={theme.palette.primary.main}
                  title="Gross Listed Value"
                  amount={rev.grossListedValue}
                  subtitle="MRP × qty (all orders)"
                />
              </Grid> */}
            </Grid>
          </Grid>

          {/* ── Costs Section ─────────────────────────────────────────────── */}
          <Grid item xs={12}>
            <SectionHeader title="Costs" subtitle="Cost of goods sold + marketing spend" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  icon={<CostIcon />}
                  iconColor={theme.palette.error.main}
                  title="Delivered Cost (COGS)"
                  amount={costs.deliveredCost}
                  subtitle={`${stats.totalDelivered ?? 0} payment-confirmed delivered orders`}
                  highlight
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  icon={<MoneyOffIcon />}
                  iconColor={theme.palette.error.light}
                  title="Returned Cost"
                  amount={costs.returnedCost}
                  subtitle={`${stats.totalReturns ?? 0} payment-confirmed returned orders`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  icon={<CostIcon />}
                  iconColor={theme.palette.error.dark}
                  title="Total Cost of Goods"
                  amount={costs.totalCostOfGoods}
                  subtitle="All orders incl. pending & in-transit"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  icon={<MarketingIcon />}
                  iconColor={theme.palette.secondary.main}
                  title="Marketing & Ad Expense"
                  amount={costs.marketingExpense}
                  subtitle={`${costs.marketingCount ?? 0} entries`}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* ── Meesho Deductions Section ──────────────────────────────────── */}
          <Grid item xs={12}>
            <SectionHeader
              title="Meesho Deductions"
              subtitle="Already deducted from settlement — shown for reference only"
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <MetricCard
                  icon={<CommissionIcon />}
                  iconColor={theme.palette.warning.main}
                  title="Commission"
                  amount={deductions.commission}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <MetricCard
                  icon={<ShippingIcon />}
                  iconColor={theme.palette.info.main}
                  title="Shipping Charges"
                  amount={deductions.shippingCharge}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <MetricCard
                  icon={<ReturnIcon />}
                  iconColor={theme.palette.warning.dark}
                  title="Return Shipping"
                  amount={deductions.returnShippingCharge}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <MetricCard
                  icon={<TaxIcon />}
                  iconColor={theme.palette.info.dark}
                  title="TDS"
                  amount={deductions.tds}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <MetricCard
                  icon={<TaxIcon />}
                  iconColor={theme.palette.info.dark}
                  title="TCS"
                  amount={deductions.tcs}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <MetricCard
                  icon={<MoneyOffIcon />}
                  iconColor={theme.palette.error.main}
                  title="Total Deductions"
                  amount={deductions.total}
                  highlight
                />
              </Grid>
            </Grid>
          </Grid>

          {/* ── Pending Section ───────────────────────────────────────────── */}
          {(stats.pendingOrders ?? 0) > 0 && (
            <Grid item xs={12} md={4}>
              <SectionHeader title="Pending" />
              <MetricCard
                icon={<PendingIcon />}
                iconColor={theme.palette.warning.main}
                title="Pending Payment Orders"
                amount={pending.estimatedValue}
                subtitle={`${stats.pendingOrders ?? 0} orders `}
              />
            </Grid>
          )}

          {/* ── P&L Breakdown Table ───────────────────────────────────────── */}
          <Grid item xs={12} md={pending.orderCount > 0 ? 8 : 12}>
            <SectionHeader title="P&L Breakdown" />
            <Card elevation={1}>
              <CardContent sx={{ p: 0 }}>
                {[
                  {
                    label: "Net Settlement",
                    desc: "Amount received from Meesho (after their deductions)",
                    value: pl.netSettlement,
                    sign: "+",
                    color: theme.palette.success.main,
                  },
                  {
                    label: "Delivered Cost (COGS)",
                    desc: "Cost price × delivered order quantity",
                    value: pl.deliveredCost,
                    sign: "−",
                    color: theme.palette.error.main,
                  },
                  {
                    label: "Marketing & Ad Expense",
                    desc: `${costs.marketingCount ?? 0} entries`,
                    value: pl.marketingExpense,
                    sign: "−",
                    color: theme.palette.error.main,
                  },
                ].map((row, i) => (
                  <Box key={i}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 3,
                        py: 1.75,
                        "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.04) },
                      }}
                    >
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                          <Typography
                            component="span"
                            sx={{ color: row.color, fontWeight: 800, mr: 1, fontSize: "1.05rem" }}
                          >
                            {row.sign}
                          </Typography>
                          {row.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, mt: 0.25 }}>
                          {row.desc}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: row.color, fontSize: "1rem" }}>
                        {fmtDec(row.value)}
                      </Typography>
                    </Box>
                    <Divider />
                  </Box>
                ))}

                {/* Final result row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 3,
                    py: 2,
                    bgcolor: alpha(plColor, 0.06),
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    {isProfit ? (
                      <ArrowUpIcon sx={{ color: plColor, fontSize: 28 }} />
                    ) : isLoss ? (
                      <ArrowDownIcon sx={{ color: plColor, fontSize: 28 }} />
                    ) : (
                      <BreakEvenIcon sx={{ color: plColor, fontSize: 28 }} />
                    )}
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Final P&L
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                        Net Settlement − Delivered Cost − Marketing
                      </Typography>
                    </Box>
                    <Chip
                      label={pl.status || "N/A"}
                      size="small"
                      sx={{ bgcolor: plColor, color: "#fff", fontWeight: 700, fontSize: "0.8rem" }}
                    />
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: plColor }}>
                    {fmtDec(pl.amount)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      )}
    </Box>
  );
};

export default FinanceDashboard;
